import type { Listing } from "@/data/listings";
import type { ScheduleSlot } from "@/utils/scheduleSlots";

import { apiFetch } from "./client";

import { slugify } from "@/utils/seo";

/**
 * Libellé unique du badge « premier cours ».
 *
 * Le professeur choisit oui ou non, pas le texte. Le backend impose la même
 * valeur dans `normalizeProfileSections` : celle-ci ne sert qu'à l'afficher
 * avant l'aller-retour serveur, et comme repli pour les annonces
 * enregistrées avant le changement, dont le libellé peut être vide.
 */
export const FIRST_LESSON_LABEL = "Premier cours offert";

type ApiListing = {
  _id: string;
  ownerId?:
    | string
    | {
        _id?: string;
        name?: string;
        avatarUrl?: string;
      };
  owner?: {
    _id?: string;
    name?: string;
    avatarUrl?: string;
  };
  ownerName?: string;
  ownerAvatarUrl?: string;
  title: string;
  subject: string;
  city: string;
  price: number;
  slotPricingEnabled?: boolean;
  rating?: number;
  reviews?: number;
  tags?: string[];
  experience?: string;
  availability?: string;
  description?: string;
  isOnline?: boolean;
  studentBookingDefaultZoomEnabled?: boolean;
  isFeatured?: boolean;
  featuredRank?: number;
  scheduleSlots?: ScheduleSlot[];
  profileSections?: {
    aboutMe?: string;
    studentLevels?: string[];
    lessonFormats?: string[];
    firstLesson?: {
      enabled?: boolean;
      label?: string;
    };
  };
  slug?: string;
  status?: "draft" | "active";
};

function normalizeListing(listing: ApiListing): Listing {
  const ownerFromOwnerId =
    listing.ownerId && typeof listing.ownerId === "object"
      ? listing.ownerId
      : undefined;
  const ownerFromOwner = listing.owner;
  const ownerName =
    listing.ownerName ?? ownerFromOwnerId?.name ?? ownerFromOwner?.name ?? "";
  const ownerId =
    typeof listing.ownerId === "string"
      ? listing.ownerId
      : (ownerFromOwnerId?._id ?? ownerFromOwner?._id);
  const ownerAvatarUrl =
    listing.ownerAvatarUrl ??
    ownerFromOwnerId?.avatarUrl ??
    ownerFromOwner?.avatarUrl;
  const fallbackTutorSlug = (() => {
    const nameParts = String(ownerName || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const firstName = nameParts[0] || "professeur";
    const familyInitial = nameParts[1]?.[0] || "";
    const ownerSlug = slugify(
      familyInitial ? `${firstName} ${familyInitial}` : firstName,
    );
    const titleSlug = slugify(listing.title || "");

    return titleSlug ? `${ownerSlug}-${titleSlug}` : ownerSlug;
  })();
  const slug = listing.slug ?? fallbackTutorSlug;

  const resolvedId = listing._id;

  return {
    _id: resolvedId,
    slug,
    ownerName,
    ownerId,
    ownerAvatarUrl,
    title: listing.title,
    subject: listing.subject,
    city: listing.city,
    price: listing.price,
    slotPricingEnabled: listing.slotPricingEnabled ?? false,
    rating: listing.rating ?? 0,
    reviews: listing.reviews ?? 0,
    tags: listing.tags ?? [],
    experience: listing.experience ?? "",
    availability: listing.availability ?? "",
    description: listing.description ?? "",
    isOnline: listing.isOnline ?? false,
    studentBookingDefaultZoomEnabled:
      listing.studentBookingDefaultZoomEnabled ?? false,
    isFeatured: listing.isFeatured ?? false,
    featuredRank: listing.featuredRank ?? 999,
    scheduleSlots: listing.scheduleSlots ?? [],
    profileSections: {
      aboutMe: listing.profileSections?.aboutMe ?? "",
      studentLevels: listing.profileSections?.studentLevels ?? [],
      lessonFormats: listing.profileSections?.lessonFormats ?? [],
      firstLesson: {
        enabled: listing.profileSections?.firstLesson?.enabled ?? false,
        label: listing.profileSections?.firstLesson?.label ?? "",
      },
    },
    status: listing.status ?? "active",
  };
}

type ListingsResponse = {
  items: ApiListing[];
  total: number;
  page: number;
  limit: number;
};

type ListingsParams = {
  page?: number;
  limit?: number;
  city?: string;
  subject?: string;
  ownerId?: string;
  featured?: boolean;
  isOnline?: boolean;
  minPrice?: number;
  maxPrice?: number;
  minRating?: number;
  search?: string;
  sortBy?:
    | "relevance"
    | "price"
    | "rating"
    | "newest"
    | "reviews"
    | "featuredRank";
  sortDir?: "asc" | "desc";
  mine?: boolean;
  status?: "draft" | "active";
};

function buildListingsQuery(params?: ListingsParams) {
  const searchParams = new URLSearchParams();

  if (!params) return searchParams;
  if (params.page) searchParams.set("page", `${params.page}`);
  if (params.limit) searchParams.set("limit", `${params.limit}`);
  if (params.city) searchParams.set("city", params.city);
  if (params.subject) searchParams.set("subject", params.subject);
  if (params.ownerId) searchParams.set("ownerId", params.ownerId);
  if (params.featured) searchParams.set("featured", "true");
  if (params.isOnline) searchParams.set("isOnline", "true");
  if (typeof params.minPrice === "number") {
    searchParams.set("minPrice", `${params.minPrice}`);
  }
  if (typeof params.maxPrice === "number") {
    searchParams.set("maxPrice", `${params.maxPrice}`);
  }
  if (typeof params.minRating === "number") {
    searchParams.set("minRating", `${params.minRating}`);
  }
  if (params.search) searchParams.set("search", params.search);
  if (params.sortBy) searchParams.set("sortBy", params.sortBy);
  if (params.sortDir) searchParams.set("sortDir", params.sortDir);
  if (params.status) searchParams.set("status", params.status);

  return searchParams;
}

export async function fetchListings(params?: ListingsParams) {
  const query = buildListingsQuery(params);
  const baseUrl = params?.mine ? "/api/listings/me" : "/api/listings";
  const url = query.toString() ? `${baseUrl}?${query}` : baseUrl;
  const data = await apiFetch<ListingsResponse | ApiListing[]>(url);

  if (Array.isArray(data)) {
    const items = data.map(normalizeListing);

    return {
      items,
      total: items.length,
      page: 1,
      limit: items.length,
    };
  }

  return {
    items: data.items.map(normalizeListing),
    total: data.total,
    page: data.page,
    limit: data.limit,
  };
}

export async function fetchListingsByOwner(ownerId: string) {
  const data = await fetchListings({ ownerId, limit: 2000, page: 1 });

  return data.items;
}

export async function fetchListingById(listingId: string) {
  const data = await apiFetch<ApiListing>(`/api/listings/${listingId}`);

  return normalizeListing(data);
}

export type ListingAvailabilitySlot = {
  day: string;
  start: string;
  end: string;
};

export async function fetchListingAvailability(
  listingId: string,
  date: string,
) {
  const params = new URLSearchParams({ date });

  return apiFetch<{ date: string; slots: ListingAvailabilitySlot[] }>(
    `/api/listings/${listingId}/availability?${params}`,
  );
}

export async function fetchListingBySlug(params: {
  city: string;
  subject: string;
  tutorSlug: string;
}) {
  const searchParams = new URLSearchParams({
    city: params.city,
    subject: params.subject,
    tutorSlug: params.tutorSlug,
  });
  const data = await apiFetch<ApiListing>(
    `/api/listings/lookup?${searchParams}`,
  );

  return normalizeListing(data);
}

type ListingPayload = {
  title: string;
  subject: string;
  city: string;
  price: number;
  slotPricingEnabled?: boolean;
  description?: string;
  isOnline?: boolean;
  studentBookingDefaultZoomEnabled?: boolean;
  scheduleSlots?: ScheduleSlot[];
  profileSections?: {
    aboutMe?: string;
    studentLevels?: string[];
    lessonFormats?: string[];
    firstLesson?: {
      enabled?: boolean;
      label?: string;
    };
  };
  status?: "draft" | "active";
};

export async function createListing(payload: ListingPayload) {
  const data = await apiFetch<ApiListing>("/api/listings", {
    method: "POST",
    body: JSON.stringify(payload),
  });

  return normalizeListing(data);
}

export async function updateListing(
  listingId: string,
  payload: ListingPayload,
) {
  const data = await apiFetch<ApiListing>(`/api/listings/${listingId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

  return normalizeListing(data);
}

export async function updateListingStatus(
  listingId: string,
  status: "draft" | "active",
) {
  const data = await apiFetch<ApiListing>(`/api/listings/${listingId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });

  return normalizeListing(data);
}

export async function deleteListing(listingId: string) {
  return apiFetch<void>(`/api/listings/${listingId}`, {
    method: "DELETE",
  });
}

export type ListingReview = {
  id: string;
  rating: number;
  message: string;
  name: string;
  ratedAt: string;
};

export async function fetchListingReviews(listingId: string) {
  return apiFetch<ListingReview[]>(`/api/listings/${listingId}/reviews`);
}
